import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

// Get backend URL for WebSocket connection
const getSocketUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Remove /api prefix if present and use base URL
  return backendUrl.replace('/api', '');
};

export const WebSocketProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const socketRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Notification toast icons based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      // Task notifications
      case 'task_assigned':
      case 'assignment':
        return '📋';
      case 'approval_requested':
      case 'approval':
        return '⏳';
      case 'task_approved':
        return '✅';
      case 'task_rejected':
        return '❌';
      case 'status_change':
      case 'dev_status_change':
      case 'design_status_change':
        return '🔄';
      case 'brand_change':
        return '🏷️';
      // Social media notifications
      case 'social_post':
      case 'social':
        return '📱';
      case 'social_status_change':
        return '📊';
      case 'social_assigned':
      case 'social_assignment':
        return '👤';
      case 'social_scheduled':
        return '📅';
      case 'social_published':
        return '🎉';
      default:
        return '🔔';
    }
  };

  // Get toast background color based on notification type
  const getNotificationColor = (type) => {
    if (type?.includes('approved') || type?.includes('published')) {
      return 'border-l-4 border-l-green-500';
    } else if (type?.includes('rejected') || type?.includes('Not Approved')) {
      return 'border-l-4 border-l-red-500';
    } else if (type?.includes('social')) {
      return 'border-l-4 border-l-purple-500';
    } else if (type?.includes('approval') || type?.includes('pending')) {
      return 'border-l-4 border-l-amber-500';
    } else if (type?.includes('status')) {
      return 'border-l-4 border-l-blue-500';
    }
    return 'border-l-4 border-l-cyan-500';
  };

  // Handle incoming notification
  const handleNotification = useCallback((payload) => {
    const { type, data } = payload;
    
    console.log('Received notification:', type, data);
    
    // Increment unread count
    setNewNotificationCount(prev => prev + 1);
    
    // Show toast notification with auto-dismiss
    const icon = getNotificationIcon(type);
    const colorClass = getNotificationColor(type);
    
    toast(
      <div className={`flex items-start gap-3 ${colorClass} pl-3 -ml-3`}>
        <span className="text-xl">{icon}</span>
        <div>
          <p className="font-semibold text-slate-900">{data.title}</p>
          <p className="text-sm text-slate-600">{data.message}</p>
          {data.actor_name && (
            <p className="text-xs text-slate-500 mt-1">by {data.actor_name}</p>
          )}
        </div>
      </div>,
      {
        duration: 5000,
        className: 'bg-white border border-slate-200 shadow-lg',
        position: 'top-right',
      }
    );

    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('new-notification', { detail: { type, data } }));
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token || socketRef.current?.connected) return;

    const socketUrl = getSocketUrl();
    console.log('Connecting to WebSocket at:', socketUrl);

    const socket = io(socketUrl, {
      path: '/api/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    socket.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      reconnectAttempts.current = 0;
    });

    socket.on('connected', (data) => {
      console.log('Connection confirmed:', data);
    });

    socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      reconnectAttempts.current += 1;
      
      if (reconnectAttempts.current >= maxReconnectAttempts) {
        console.log('Max reconnection attempts reached');
      }
    });

    // Listen for notifications
    socket.on('notification', handleNotification);

    // Keep-alive ping
    socket.on('pong', (data) => {
      console.log('Pong received:', data);
    });

    socketRef.current = socket;
  }, [token, handleNotification]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Reset notification count (when user views notifications)
  const resetNotificationCount = useCallback(() => {
    setNewNotificationCount(0);
  }, []);

  // Connect when token is available
  useEffect(() => {
    if (token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  // Keep connection alive with periodic ping
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('ping', { timestamp: Date.now() });
      }
    }, 30000); // Ping every 30 seconds

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  const value = {
    isConnected,
    newNotificationCount,
    resetNotificationCount,
    socket: socketRef.current,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
